import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Feature } from 'src/replicant/analytics';
import { Gacha, GachaEventTheme } from 'src/replicant/gacha/gacha.ruleset';
import StateObserver, { now, user } from 'src/StateObserver';
import PopupBasicLazy from '../../components/popups/PopupBasicLazy';
import Scroll from '../../components/shared/Scroll';
import { purchaseGacha } from '../gacha.sequence';
import { GachaItem } from './GachaItem';
import { getCurrentGachaEvent } from 'src/replicant/gacha/gacha.getters';
import { gachaTheme } from '../gacha.theme';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import uiConfig from 'src/lib/ui/config';

export class PopupGachaSelect extends PopupBasicLazy {
  private scroll!: Scroll<Gacha, GachaItem>;
  private theme!: GachaEventTheme;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: 945,
      height: 1080,
      darkerBg: false,
      tracking: {
        feature: Feature.GACHA._,
        $subFeature: Feature.GACHA.SELECT_POPUP._,
      },
    });

    this.box.removeFromSuperview();
  }

  protected getAssetsGroup(): AssetGroup | null {
    return new AssetGroup(gachaTheme().getAssets());
  }

  public init({ gachas, theme }: { gachas: Gacha[]; theme: GachaEventTheme }) {
    // Need to set this before super.init to be available for load assets
    this.theme = theme;

    super.init({});

    this.createPopup();
    this.scroll.setItems(gachas);
  }

  private createPopup() {
    const { width, height } = this.getView().style;

    const view = new View({
      superview: this.getView(),
      x: width / 2,
      y: height / 2,
      width,
      height,
      offsetX: -width / 2,
      offsetY: -height / 2,
      centerAnchor: true,
      visible: false,
    });

    this.box = view;

    new ImageScaleView({
      superview: view,
      x: width / 2 - 12,
      y: height / 2,
      width: 714,
      height: 912,
      centerOnOrigin: true,
      ...gachaTheme().getStyle('gacha_popup', { width, height }),
    });

    this.buttonClose.updateOpts({
      superview: view,
      x: 650,
      y: 310,
    });

    const titleY = height * 0.24;
    const titleX = width / 2;
    const locale = StateObserver.getLocale();

    const title = new ImageScaleView({
      superview: view,
      x: width / 2,
      y: 335,
      scaleMethod: 'contain',
      width: 504,
      height: 174,
      centerOnOrigin: true,
      ...gachaTheme().getStyle(`title_${locale}`, { titleY, titleX }),
    });
    new ImageScaleView({
      superview: view,
      x: width * 0.5,
      y: titleY + 120,
      width: 267,
      height: 87,
      scaleMethod: 'contain',
      verticalAlign: 'center',
      horizontalAlign: 'center',
      centerOnOrigin: true,
      ...gachaTheme().getStyle(`intro_subtitle_${locale}`, { titleY }),
    });

    const eventEnd = new Date(getCurrentGachaEvent(user(), now())?.end ?? 0);
    const yyyy = eventEnd?.getFullYear();
    const M = (eventEnd?.getMonth() + 1).toString().padStart(2, '0');
    const D = eventEnd?.getDate().toString().padStart(2, '0');
    const h = eventEnd?.getHours().toString().padStart(2, '0');
    const mm = eventEnd?.getMinutes().toString().padStart(2, '0');

    new LangBitmapFontTextView({
      superview: view,
      x: width / 2,
      y: 705,
      centerOnOrigin: true,
      width: 600,
      height: 38,
      font: 'Body',
      size: 26,
      color: '#A8896C',
      align: 'center',
      verticalAlign: 'center',
      ...gachaTheme().getStyle('gacha_popup_timer'),
      localeText: () =>
        i18n('gacha.until', {
          yyyy,
          M,
          D,
          h,
          mm,
        }),
    });

    this.scroll = new Scroll({
      superview: view,
      createItem: this.createItem.bind(this),
      rect: {
        x: width / 2,
        y: 890,
        width: 530,
        height: 320,
        centerOnOrigin: true,
      },
    });

    // Info button
    new ButtonScaleView({
      superview: view,
      ...uiConfig.buttons.info,
      width: 54,
      height: 54,
      x: 600,
      y: 715,
      tracking: {
        feature: Feature.GACHA._,
        $subFeature: Feature.GACHA.OPEN_INFO,
      },
      onClick: async () => openPopupPromise('popupGachaInfo', {}),
    });
  }

  private createItem(superview: View, _: number, data: Gacha) {
    return new GachaItem({
      superview,
      title: i18n(`gacha.gachas.${data.num_spins === 1 ? 'single' : '10'}`),
      newPrice: data.price,
      oldPrice: data.ref_price,
      currency: data.currency,
      single: data.num_spins === 1,
      tracking: {
        feature: Feature.GACHA._,
        $subFeature: Feature.GACHA.SELECT_POPUP.ITEM,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        this.opts.close();
        await purchaseGacha(data);
      },
    });
  }
}
